import type { ProductWithBundleId } from '~/models/CampaignModel';
import { ProductType } from '~/models/product/ProductType';
import { BYOD_COMMERCIAL_PRODUCT_ID, BYOD_PRODUCT_ID } from '~/store/campaign/byod.constants';
import type { CheckoutProduct } from '~/store/checkout/api';
import { DEFAULT_TV_CHANNEL_IDS } from '../constants';
import type { FlexipakkenChoiceSelection } from '../useBasket';

/**
 * Products that only exist in the client to drive UI state, e.g. "Eget utstyr" (BYOD).
 * They have no counterpart in the backend product catalog, so they must never end up in the checkout payload.
 *
 * Matched on the commercial product id, since that is the value the backend rejects. The client-side id is
 * checked as well, to also catch a placeholder that is rebuilt without its fake commercial product id.
 */
const clientSideOnlyProductIds: string[] = [BYOD_PRODUCT_ID];
const clientSideOnlyCommercialProductIds: number[] = [BYOD_COMMERCIAL_PRODUCT_ID];

export const isClientSideOnlyProduct = (product: Pick<ProductWithBundleId, 'id' | 'offer'>): boolean =>
  clientSideOnlyProductIds.includes(product.id) || clientSideOnlyCommercialProductIds.includes(product.offer.commercialProductId);

const getChannelIds = (
  product: ProductWithBundleId,
  flexipakkenChoice: FlexipakkenChoiceSelection | undefined,
): number[] => {
  if (product.productType === ProductType.Subscription && flexipakkenChoice) {
    if (flexipakkenChoice.kind === 'tv-channels') {
      return DEFAULT_TV_CHANNEL_IDS;
    } else if (flexipakkenChoice.kind === 'streaming' && flexipakkenChoice.channelId != null) {
      return [flexipakkenChoice.channelId];
    }
    return [];
  }
  return product.offer.channelIds ?? [];
};

/** Maps the basket to the products part of the checkout payload, dropping client-side-only products. */
export const toCheckoutProducts = (
  selectedProducts: ProductWithBundleId[],
  flexipakkenChoice: FlexipakkenChoiceSelection | undefined,
): CheckoutProduct[] =>
  selectedProducts
    .filter((product) => !isClientSideOnlyProduct(product))
    .map((product) => ({
      commercialProductId: product.offer.commercialProductId,
      contractPeriodId: product.offer.contractPeriod,
      offerId: product.offer.offerId,
      channelIds: getChannelIds(product, flexipakkenChoice),
    }));
